import React, { useState, useEffect } from 'react';
import { Calendar, Plus, CheckCircle, AlertCircle, ClipboardList } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { EmptyState } from '../../components/ui';
import { operationsAPI } from '../../services/api';
import { format } from 'date-fns';
import OperationsTabs from './OperationsTabs';

const ACTIVITY_LABELS = {
  PITCH_PREP: 'Pitch Prep',
  CLEANING: 'Cleaning',
  WATERING: 'Watering',
  LIGHT_CHECK: 'Light Check',
  NET_FIX: 'Net Fix',
};

const STATUS_COLORS = {
  COMPLETED: 'bg-ga-success/15 text-ga-success border-ga-success/30',
  PENDING: 'bg-ga-warning/15 text-ga-warning border-ga-warning/30',
  ISSUE_FOUND: 'bg-ga-danger/15 text-ga-danger border-ga-danger/30',
};

const STATUS_FALLBACK = 'bg-ga-bg-surface-subtle text-ga-text-muted border-ga-border-subtle';

export default function DailyOperations() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    log_date: new Date().toISOString().slice(0, 10),
    activity_type: 'PITCH_PREP',
    description: '',
    status: 'PENDING',
  });

  const fetchList = () => {
    setLoading(true);
    operationsAPI.getDaily(date).then((res) => {
      setList(res.data || []);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchList(); }, [date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await operationsAPI.addDaily({ ...form, log_date: date });
      setShowForm(false);
      setForm({ log_date: date, activity_type: 'PITCH_PREP', description: '', status: 'PENDING' });
      fetchList();
    } catch (err) {
      alert(err.message || 'Failed to add');
    }
  };

  const handleMarkCompleted = async (log) => {
    if (log.status === 'COMPLETED') return;
    try {
      setUpdatingId(log.id);
      const res = await operationsAPI.updateDaily(log.id, { status: 'COMPLETED' });
      if (res?.data) setList((prev) => prev.map((l) => (l.id === log.id ? { ...l, ...res.data } : l)));
      else fetchList();
    } catch (err) {
      alert(err.message || 'Failed to update');
    } finally {
      setUpdatingId(null);
    }
  };

  const openAddForm = () => {
    setForm((f) => ({ ...f, log_date: date }));
    setShowForm(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
              <Calendar className="h-7 w-7 text-ga-primary" />
              Daily Operations Log
            </h1>
            <p className="text-ga-text-muted mt-1">Track facility maintenance tasks</p>
          </div>
          <div className="flex gap-2">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              aria-label="Log date"
              className="ga-input px-4 py-2 rounded-lg"
            />
            <button
              onClick={openAddForm}
              className="btn-primary-solid inline-flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Add Log
            </button>
          </div>
        </div>

        <OperationsTabs active="daily" />

        {showForm && (
          <div className="ga-card rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-ga-text-primary mb-4">Add Daily Operation</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="daily-activity-type" className="block text-sm font-medium text-ga-text-muted mb-1">Activity Type</label>
                <select
                  id="daily-activity-type"
                  value={form.activity_type}
                  onChange={(e) => setForm((f) => ({ ...f, activity_type: e.target.value }))}
                  className="ga-input w-full px-4 py-2 rounded-lg"
                >
                  {Object.entries(ACTIVITY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="daily-description" className="block text-sm font-medium text-ga-text-muted mb-1">Description</label>
                <textarea
                  id="daily-description"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="ga-input w-full px-4 py-2 rounded-lg"
                />
              </div>
              <div>
                <label htmlFor="daily-status" className="block text-sm font-medium text-ga-text-muted mb-1">Status</label>
                <select
                  id="daily-status"
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  className="ga-input w-full px-4 py-2 rounded-lg"
                >
                  <option value="COMPLETED">Completed</option>
                  <option value="PENDING">Pending</option>
                  <option value="ISSUE_FOUND">Issue Found</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="btn-primary-solid">
                  Save
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="ga-btn-ghost px-4 py-2 rounded-lg">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="ga-card rounded-xl shadow-sm border overflow-hidden">
            <div className="py-12 text-center text-ga-text-muted">Loading...</div>
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            card
            icon={ClipboardList}
            title="No activities logged"
            description={`Nothing has been logged for ${format(new Date(date), 'dd MMM yyyy')} yet.`}
            action={
              <button onClick={openAddForm} className="btn-primary-solid inline-flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Log an activity
              </button>
            }
          />
        ) : (
          <div className="ga-card rounded-xl shadow-sm border overflow-hidden p-0 md:p-0">
            <div className="divide-y divide-ga-border-subtle">
              {list.map((log) => (
                <div key={log.id} className="p-4 flex flex-wrap items-center gap-3 sm:gap-4">
                  <div className={`p-2 rounded-ga-sm border shrink-0 ${STATUS_COLORS[log.status] || STATUS_FALLBACK}`}>
                    {log.status === 'COMPLETED' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-ga-text-primary">{ACTIVITY_LABELS[log.activity_type] || log.activity_type}</p>
                    <p className="text-sm text-ga-text-muted mt-0.5">{log.description || '—'}</p>
                    <p className="text-xs text-ga-text-muted mt-0.5">
                      {log.reported_by_name ? `By ${log.reported_by_name}` : ''}
                      {log.created_at && ` • Started ${format(new Date(log.created_at), 'HH:mm')}`}
                      {log.completed_at && ` • Completed ${format(new Date(log.completed_at), 'HH:mm')}`}
                    </p>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded-ga-full border text-xs font-medium ${STATUS_COLORS[log.status] || STATUS_FALLBACK}`}>
                    {log.status?.replace('_', ' ')}
                  </span>
                  {log.status !== 'COMPLETED' && (
                    <button
                      type="button"
                      onClick={() => handleMarkCompleted(log)}
                      disabled={updatingId === log.id}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 btn-primary-solid text-sm"
                    >
                      <CheckCircle className="h-4 w-4" />
                      {updatingId === log.id ? 'Updating…' : 'Mark completed'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
